import React, { createContext, useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../api/axios';

const CartContext = createContext();

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    const stored = localStorage.getItem('ca_cart');
    return stored ? JSON.parse(stored) : [];
  });

  useEffect(() => {
    localStorage.setItem('ca_cart', JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    let cancelled = false;

    const repairMissingVariants = async () => {
      if (!items.some((item) => !item.variantId)) return;

      const repairedItems = await Promise.all(items.map(async (item) => {
        if (item.variantId) return item;

        try {
          const { data: product } = await api.get(`/products/${item.productId}`);
          if (product.productType !== 'variable') return item;

          const variant = product.variants?.find((option) => option.stock >= item.qty);
          if (!variant) return item;

          return {
            ...item,
            variantId: variant._id,
            variantLabel: Object.entries(variant.attributes || {})
              .map(([key, value]) => `${key}: ${value}`)
              .join(', '),
            image: variant.image || item.image,
            price: variant.price,
            maxStock: variant.stock,
          };
        } catch (_) {
          return item;
        }
      }));

      if (!cancelled && repairedItems.some((item, index) => item !== items[index])) {
        setItems(repairedItems);
      }
    };

    repairMissingVariants();
    return () => { cancelled = true; };
    // Existing local carts only need this migration once when the provider mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cartKey = (productId, variantId) => `${productId}_${variantId || 'base'}`;

  const addItem = (item, { notify = true } = {}) => {
    setItems((prev) => {
      const key = cartKey(item.productId, item.variantId);
      const existing = prev.find((p) => cartKey(p.productId, p.variantId) === key);
      if (existing) {
        return prev.map((p) =>
          cartKey(p.productId, p.variantId) === key
            ? { ...p, qty: Math.min(p.qty + item.qty, item.maxStock || 999) }
            : p
        );
      }
      return [...prev, item];
    });
    if (notify) toast.success(`${item.name} added to cart`);
  };

  const addToCart = (product, qty = 1, variant = null, options = {}) => {
    const selectedVariant = variant || (product.variantId ? {
      _id: product.variantId,
      image: product.variantImage,
      price: product.price,
      stock: product.stock,
      attributes: product.variantAttributesMap || null,
      label: product.variantName,
    } : null);
    const item = {
      productId: product._id,
      variantId: selectedVariant?._id || null,
      name: product.name,
      variantLabel: selectedVariant
        ? selectedVariant.label || Object.entries(selectedVariant.attributes || {}).map(([k, v]) => `${k}: ${v}`).join(', ')
        : '',
      image: selectedVariant?.image || product.images?.[0] || '/assets/ganesha_idol.jpg',
      price: selectedVariant?.price || product.price,
      qty,
      maxStock: selectedVariant?.stock || product.stock || 99,
    };
    addItem(item, options);
  };

  const updateQty = (productId, variantId, qty) => {
    setItems((prev) =>
      prev.map((p) =>
        cartKey(p.productId, p.variantId) === cartKey(productId, variantId) ? { ...p, qty } : p
      )
    );
  };

  const removeItem = (productId, variantId) => {
    setItems((prev) => prev.filter((p) => cartKey(p.productId, p.variantId) !== cartKey(productId, variantId)));
  };

  const clearCart = () => setItems([]);

  const itemsTotal = items.reduce((sum, i) => sum + i.price * i.qty, 0);
  const itemsCount = items.reduce((sum, i) => sum + i.qty, 0);

  return (
    <CartContext.Provider value={{ items, addItem, addToCart, updateQty, removeItem, clearCart, itemsTotal, itemsCount }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
